import { useGetClassQuery, useGetUserDetailsByIdQuery, useUpdateStudentDifficultyMutation, useGetStudentOverviewResultQuery } from "util/rtk-api"

import { Difficulty, SECTIONS, normalizeSection } from "@all-for-one/shared-types"
import Breadcrumbs from "components/breadcrumb"
import { DifficultyDropdown } from "components/tables/difficulty-dropdown"
import { StudentOverviewTable } from "student-overview/table"
import { StudentContextProvider } from "student-overview/context"
import { ClassContextProvider } from "components/class-context"
import styles from "css/pages/student-overview.module.css"
import shell from "css/components/page-shell.module.css"
import React, { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { StudentEntry } from "@all-for-one/shared-types"
import { DownloadButton } from "components/blue-button"
import { DownloadActionDropdown } from "components/action-dropdown"
import { exportTableToXlsx, exportTableToPdf, svgUrlToPngDataUrl } from "util/export"
import firecrackerLogo from "img/logo-mark.png"

export const StudentOverviewPage: React.FC = () => {
    const { classId, studentId } = useParams()
    const [updateStudentDifficulty, { isLoading: isUpdatingDifficulty }] = useUpdateStudentDifficultyMutation()
    const { data: classDetails, isLoading: isClassLoading, error: classError } = useGetClassQuery(
        { classId }
    )
    const { data: studentUser, isLoading: isStudentLoading, error: studentError } = useGetUserDetailsByIdQuery(
        studentId
    )
    const { data: studentOverviewResults } = useGetStudentOverviewResultQuery({ classId: classId!, studentId: studentId! })
    const [showDownloadDropdown, setShowDownloadDropdown] = useState(false)
    const downloadContainerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node
            if (showDownloadDropdown && downloadContainerRef.current && !downloadContainerRef.current.contains(target)) {
                setShowDownloadDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [showDownloadDropdown])

    if (isClassLoading || isStudentLoading) {
        return <div>Loading...</div>
    }

    if (classError || studentError) {
        console.error('API Errors:', { classError, studentError })
        return <div>Error loading data</div>
    }

    const studentName = studentUser?.lastName + ", " + studentUser?.firstName
    const studentEntry = classDetails?.students.find((student: StudentEntry) => student.studentId === studentId)

    const handleDifficultyChange = async (newDifficulty) => {
        try {
            await updateStudentDifficulty({
                classId,
                studentId,
                difficulty: newDifficulty
            }).unwrap()

            // Optional: Show success message
            console.log('Difficulty updated successfully')
        } catch (error) {
            // Handle error - the optimistic update will be automatically reverted
            console.error('Failed to update difficulty:', error)
            // You could show a toast notification here
        }
    }

    const buildDownloadRows = () => {
        const headers = ['Lesson Name', 'Level', 'Overall %', 'Pre-Assessment %', 'Check for Understanding %', 'Independent Work %', 'Post-Assessment %']
        const rows = (studentOverviewResults ?? []).map(result => {
            const sectionMap: Record<string, number> = {}
            for (const { section, score } of (result.sectionScores ?? [])) {
                sectionMap[normalizeSection(section)] = score
            }
            return [
                result.lessonName,
                result.difficulty ?? '-',
                result.overallScore != null ? Number(result.overallScore).toFixed(1) : '-',
                sectionMap[SECTIONS.PRE_ASSESSMENT] != null ? Number(sectionMap[SECTIONS.PRE_ASSESSMENT]).toFixed(1) : '-',
                sectionMap[SECTIONS.CHECK_FOR_UNDERSTANDING] != null ? Number(sectionMap[SECTIONS.CHECK_FOR_UNDERSTANDING]).toFixed(1) : '-',
                sectionMap[SECTIONS.INDEPENDENT_WORK] != null ? Number(sectionMap[SECTIONS.INDEPENDENT_WORK]).toFixed(1) : '-',
                sectionMap[SECTIONS.POST_ASSESSMENT] != null ? Number(sectionMap[SECTIONS.POST_ASSESSMENT]).toFixed(1) : '-',
            ]
        })
        return { headers, rows }
    }

    const handleDownloadXLS = () => {
        const { headers, rows } = buildDownloadRows()
        const date = new Date().toLocaleDateString()
        exportTableToXlsx({
            fileName: `${studentName}_lessons.xlsx`,
            sheetName: 'Lessons',
            headers,
            rows,
            branding: { titleLines: [
                `Class Name: ${classDetails?.name ?? ''}`,
                `Student Name: ${studentName}`,
                `UserID: ${studentId ?? ''}`,
                `Download Date: ${date}`,
            ] }
        })
    }

    const handleDownloadPDF = async () => {
        const { headers, rows } = buildDownloadRows()
        const pdfHeaders = headers.map(h =>
            typeof h === 'string' ? h.replace('Pre-Assessment', 'Pre- Assessment').replace('Post-Assessment', 'Post- Assessment') : h
        )
        const date = new Date().toLocaleDateString()
        const logoDataUrl = await svgUrlToPngDataUrl(firecrackerLogo, 52, 52)
        exportTableToPdf({
            fileName: `${studentName}_lessons.pdf`,
            headers: pdfHeaders,
            rows,
            orientation: 'landscape',
            columnStyles: {
                0: { cellWidth: 100 },
                1: { cellWidth: 28 },
                2: { cellWidth: 28 },
                3: { cellWidth: 28 },
                4: { cellWidth: 28 },
                5: { cellWidth: 28 },
                6: { cellWidth: 28 },
            },
            branding: {
                logoDataUrl,
                titleLines: [
                    `Class Name: ${classDetails?.name ?? ''} | Student Name: ${studentName} | UserID: ${studentId ?? ''}`,
                    `Download Date: ${date}`,
                ]
            },
            styles: { fontSize: 9, cellPadding: 4 },
        })
    }

    const handleDownloadClick = () => {
        setShowDownloadDropdown(!showDownloadDropdown)
    }

    return (
        <StudentContextProvider initialStudent={studentUser} classId={classId}>
            <ClassContextProvider initialClass={classDetails}>
                {/* This page had no content column at all -- it sat at whatever
                    width <main> gave it, with the student's name as bare
                    unstyled text. `shell` is the column and heading treatment
                    every other full-width screen uses, derived by measurement
                    from her Grades_Grid; see css/components/page-shell.module.css.
                    Breadcrumbs stay: the screen is four levels deep and she
                    never drew it, so there is nothing to match and removing
                    navigation is not a styling fix. */}
                <div className={`${shell.container} ${styles.studentPageContainer}`}>
                    <Breadcrumbs items={[
                        { label: 'My Classrooms', path: '/classrooms' },
                        { label: classDetails?.name, path: `/classrooms/${classId}` },
                        { label: studentName }
                    ]} />
                    <div>
                        <h1 className={shell.title}>{studentName}</h1>
                        {/* No level here: this page's level is EDITABLE via the
                            dropdown below, so stating it again above would be
                            two sources for one value. */}
                        <p className={shell.sub}>{classDetails?.name}</p>
                    </div>
                    <div className={styles.row}>
                        <div className={styles.headerLeft}>
                            <DifficultyDropdown
                                currentDifficulty={studentEntry?.difficulty as Difficulty}
                                handleDifficultyChange={handleDifficultyChange}
                            />
                        </div>
                        <div className={`${styles.headerRight} `}>
                            <div className={styles.downloadContainer} ref={downloadContainerRef}>
                                <DownloadButton onClick={handleDownloadClick} />
                                {showDownloadDropdown && (
                                    <DownloadActionDropdown onDownloadXLS={handleDownloadXLS} onDownloadPDF={handleDownloadPDF} />
                                )}
                            </div>
                        </div>
                    </div>
                    <StudentOverviewTable />
                </div>
            </ClassContextProvider>
        </StudentContextProvider>
    )
}